import {
    Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel,
    Grid, IconButton, Skeleton, Switch, TextField, Tooltip, Typography,
} from "@mui/material";
import { Lock, Pencil, Plus, RotateCcw, Sparkles, Trash2 } from "lucide-react";
import moment from "moment";
import { useEffect, useState } from "react";
import { injectIntl } from "react-intl";
import { NotificationVariant } from "src/utils/enums/notification-variants.enum";
import * as TemplateService from "src/services/chatbot-flow-copilot-template.service";
import { normalizeCopilotTemplate } from "src/services/chatbot-flow-copilot-template.service";

// Item 09b (16/10/2026) — templates (pedidos prontos) do copiloto de fluxo. Valem pra todas as LLMs do contratante.
// Sistema: só administrador global edita (canEdit vem do backend); nunca exclui (só desativa); "Restaurar padrão".

const PROMPT_MAX_LENGTH = 4000;

const emptyForm = {
    id: null,
    isSystem: false,
    title: "",
    description: "",
    boxesSummary: "",
    prompt: "",
    mode: "create",
    active: true,
    sortOrder: 1000,
};

const CopilotTemplates = (props) => {
    const { intl, onShowNotification, onLoading } = props;
    const t = (id, values) => intl.formatMessage({ id }, values);

    const [loading, setLoading] = useState(true);
    const [actionLoading, setActionLoading] = useState(false);
    const [templates, setTemplates] = useState([]);
    const [form, setForm] = useState(null);
    const [confirm, setConfirm] = useState(null);

    const errorOf = (err) => err?.response?.data?.errors?.[0] ?? t("process.error");

    const load = () => {
        setLoading(true);
        return TemplateService.getAll()
            .then((result) => {
                if (result?.success) setTemplates((result.data ?? []).map(normalizeCopilotTemplate));
                else onShowNotification(result?.errors?.[0] ?? t("process.error"), NotificationVariant.ERROR);
            })
            .catch((err) => onShowNotification(errorOf(err), NotificationVariant.ERROR))
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        load();
    }, []);

    const runAction = (promise, successMessageId) => {
        setActionLoading(true);
        if (onLoading) onLoading(true);
        return promise
            .then((result) => {
                if (result && result.success === false) {
                    onShowNotification(result.errors?.[0] ?? t("process.error"), NotificationVariant.ERROR);
                    return false;
                }
                onShowNotification(t(successMessageId));
                return load().then(() => true);
            })
            .catch((err) => {
                onShowNotification(errorOf(err), NotificationVariant.ERROR);
                return false;
            })
            .finally(() => {
                setActionLoading(false);
                if (onLoading) onLoading(false);
            });
    };

    const payloadOf = (f) => ({
        title: f.title,
        description: f.description,
        boxesSummary: f.boxesSummary,
        prompt: f.prompt,
        mode: f.mode,
        active: f.active,
        sortOrder: f.sortOrder,
    });

    const handleSave = () => {
        if (!form) return;
        const promise = form.id
            ? TemplateService.update(form.id, payloadOf(form))
            : TemplateService.create(payloadOf(form));
        runAction(promise, "copilot.templates.saved").then((ok) => { if (ok) setForm(null); });
    };

    const handleToggleActive = (template) => {
        runAction(TemplateService.update(template.id, payloadOf({ ...template, active: !template.active })), "copilot.templates.saved");
    };

    const handleConfirm = () => {
        if (!confirm) return;
        const promise = confirm.type === "delete"
            ? TemplateService.remove(confirm.template.id)
            : TemplateService.restoreDefault(confirm.template.id);
        runAction(promise, confirm.type === "delete" ? "copilot.templates.deleted" : "copilot.templates.restored")
            .then(() => setConfirm(null));
    };

    const setField = (name) => (e) => {
        const value = e.target.type === "checkbox" ? e.target.checked : e.target.value;
        setForm((prev) => ({ ...prev, [name]: value }));
    };

    const renderCard = (template) => (
        <Grid item xs={12} md={6} key={template.id}>
            <Box sx={{
                border: "1px solid #e0e0e0",
                borderRadius: "12px",
                borderLeft: template.active ? "4px solid var(--mexx-green-primary, #8BC34A)" : "1px solid #e0e0e0",
                p: 2,
                height: "100%",
                opacity: template.active ? 1 : 0.6,
                display: "flex",
                flexDirection: "column",
                gap: 1,
            }}>
                <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 1 }}>
                    <Typography sx={{ fontWeight: 600, fontSize: 15 }}>{template.title}</Typography>
                    <Box sx={{ display: "flex", gap: 0.5, flexShrink: 0 }}>
                        {template.isSystem && <Chip size="small" icon={<Lock size={12} />} label={t("copilot.templates.system")} />}
                        <Chip size="small" variant="outlined"
                            label={t(template.mode === "edit" ? "copilot.templates.mode.edit" : "copilot.templates.mode.create")} />
                    </Box>
                </Box>
                <Typography sx={{ fontSize: 13, color: "#666" }}>{template.description}</Typography>
                {template.boxesSummary && (
                    <Typography sx={{ fontSize: 12, color: "#888" }}>
                        {t("copilot.templates.boxes")}: {template.boxesSummary}
                    </Typography>
                )}
                {template.updatedAt && (
                    <Typography sx={{ fontSize: 11, color: "#999" }}>
                        {t("copilot.templates.updated.by", {
                            name: template.updatedUserName || "—",
                            date: moment(template.updatedAt).format("DD/MM/YYYY HH:mm"),
                        })}
                    </Typography>
                )}
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mt: "auto", pt: 1 }}>
                    <Tooltip title={template.canEdit ? "" : t("copilot.templates.system.only.global")}>
                        <span>
                            <FormControlLabel
                                control={<Switch size="small" checked={template.active} disabled={!template.canEdit || actionLoading}
                                    onChange={() => handleToggleActive(template)} />}
                                label={<span style={{ fontSize: 13 }}>{t("copilot.templates.active")}</span>}
                            />
                        </span>
                    </Tooltip>
                    <Box>
                        <Tooltip title={template.canEdit ? t("edit") : t("copilot.templates.system.only.global")}>
                            <span>
                                <IconButton size="small" disabled={!template.canEdit || actionLoading} onClick={() => setForm({ ...template })}>
                                    <Pencil size={16} />
                                </IconButton>
                            </span>
                        </Tooltip>
                        {template.isSystem ? (
                            template.canEdit && (
                                <Tooltip title={t("copilot.templates.restore.default")}>
                                    <span>
                                        <IconButton size="small" disabled={actionLoading} onClick={() => setConfirm({ type: "restore", template })}>
                                            <RotateCcw size={16} />
                                        </IconButton>
                                    </span>
                                </Tooltip>
                            )
                        ) : (
                            <Tooltip title={t("delete")}>
                                <span>
                                    <IconButton size="small" disabled={actionLoading} onClick={() => setConfirm({ type: "delete", template })}>
                                        <Trash2 size={16} />
                                    </IconButton>
                                </span>
                            </Tooltip>
                        )}
                    </Box>
                </Box>
            </Box>
        </Grid>
    );

    return (
        <Box sx={{ mt: 2 }}>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
                <Typography variant="h6">{t("copilot.templates.title")}</Typography>
                <Button variant="outlined" startIcon={<Plus size={16} />} disabled={actionLoading} onClick={() => setForm({ ...emptyForm })}>
                    {t("copilot.templates.new")}
                </Button>
            </Box>
            <Alert severity="info" sx={{ mb: 2 }}>{t("copilot.templates.info")}</Alert>

            {loading ? (
                <Grid container spacing={2}>
                    {[0, 1, 2, 3].map((i) => (
                        <Grid item xs={12} md={6} key={i}>
                            <Skeleton variant="rectangular" animation="wave" height={150} sx={{ borderRadius: "12px" }} />
                        </Grid>
                    ))}
                </Grid>
            ) : templates.length === 0 ? (
                <Box sx={{ textAlign: "center", border: "1px solid #e0e0e0", borderRadius: "12px", p: 4 }}>
                    <Sparkles size={56} color="#bdbdbd" />
                    <Typography sx={{ fontSize: 18, fontWeight: 600, mt: 1 }}>{t("copilot.templates.empty.title")}</Typography>
                    <Typography sx={{ fontSize: 14, color: "#888", mb: 2 }}>{t("copilot.templates.empty.description")}</Typography>
                    <Button variant="contained" startIcon={<Plus size={16} />} onClick={() => setForm({ ...emptyForm })}>
                        {t("copilot.templates.new")}
                    </Button>
                </Box>
            ) : (
                <Grid container spacing={2}>{templates.map(renderCard)}</Grid>
            )}

            <Dialog open={!!form} onClose={() => !actionLoading && setForm(null)} fullWidth maxWidth="md"
                PaperProps={{ sx: { maxHeight: "90vh", borderRadius: 3 } }}>
                <DialogTitle>{form?.id ? t("copilot.templates.edit") : t("copilot.templates.new")}</DialogTitle>
                <DialogContent dividers sx={{ p: 0 }}>
                    <div style={{ maxHeight: "60vh", overflowY: "auto", padding: "16px 20px" }}>
                        {form && (
                            <Grid container spacing={2}>
                                {form.isSystem && (
                                    <Grid item xs={12}>
                                        <Alert severity="warning">{t("copilot.templates.system.edit.warning")}</Alert>
                                    </Grid>
                                )}
                                <Grid item xs={12} sm={8}>
                                    <TextField fullWidth required label={t("copilot.templates.field.title")} value={form.title}
                                        onChange={setField("title")} inputProps={{ maxLength: 150 }} />
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <TextField fullWidth select label={t("copilot.templates.field.mode")} value={form.mode}
                                        onChange={setField("mode")} SelectProps={{ native: true }} InputLabelProps={{ shrink: true }}>
                                        <option value="create">{t("copilot.templates.mode.create")}</option>
                                        <option value="edit">{t("copilot.templates.mode.edit")}</option>
                                    </TextField>
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField fullWidth multiline minRows={2} label={t("copilot.templates.field.description")}
                                        value={form.description} onChange={setField("description")} inputProps={{ maxLength: 1000 }}
                                        helperText={t("copilot.templates.field.description.helper")} />
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField fullWidth label={t("copilot.templates.field.boxes")} value={form.boxesSummary}
                                        onChange={setField("boxesSummary")} inputProps={{ maxLength: 500 }}
                                        helperText={t("copilot.templates.field.boxes.helper")} />
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField fullWidth required multiline minRows={8} label={t("copilot.templates.field.prompt")}
                                        value={form.prompt} onChange={setField("prompt")} inputProps={{ maxLength: PROMPT_MAX_LENGTH }}
                                        helperText={`${t("copilot.templates.field.prompt.helper")} (${form.prompt.length}/${PROMPT_MAX_LENGTH})`} />
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <TextField fullWidth type="number" label={t("copilot.templates.field.order")} value={form.sortOrder}
                                        onChange={setField("sortOrder")} inputProps={{ min: 0 }} />
                                </Grid>
                                <Grid item xs={12} sm={8} sx={{ display: "flex", alignItems: "center" }}>
                                    <FormControlLabel control={<Switch checked={form.active} onChange={setField("active")} />}
                                        label={t("copilot.templates.active")} />
                                </Grid>
                            </Grid>
                        )}
                    </div>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setForm(null)} disabled={actionLoading}>{t("cancel")}</Button>
                    <Button variant="contained" onClick={handleSave}
                        disabled={actionLoading || !form?.title?.trim() || !form?.prompt?.trim()}>
                        {t("save")}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={!!confirm} onClose={() => !actionLoading && setConfirm(null)} maxWidth="xs" fullWidth>
                <DialogTitle>
                    {confirm?.type === "delete" ? t("copilot.templates.delete.title") : t("copilot.templates.restore.title")}
                </DialogTitle>
                <DialogContent>
                    <Typography sx={{ fontSize: 14 }}>
                        {confirm?.type === "delete"
                            ? t("copilot.templates.delete.confirm", { title: confirm?.template?.title })
                            : t("copilot.templates.restore.confirm", { title: confirm?.template?.title })}
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirm(null)} disabled={actionLoading}>{t("cancel")}</Button>
                    <Button variant="contained" color={confirm?.type === "delete" ? "error" : "primary"} onClick={handleConfirm} disabled={actionLoading}>
                        {confirm?.type === "delete" ? t("delete") : t("copilot.templates.restore.default")}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default injectIntl(CopilotTemplates);
